import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useGoogleLogin } from '@react-oauth/google';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import appleIcon from '../../assets/apple_icon.svg';
import googleIcon from '../../assets/google_icon.svg';
import { useAuth } from '../../context/AuthContext';

const SignupModal = ({ open, onClose, onSwitchToLogin, prefilledPhone = '' }) => {
  const { signup, googleLogin, appleLogin } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone_number: prefilledPhone,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Sync prefilledPhone when it changes
  React.useEffect(() => {
    if (prefilledPhone) {
      setFormData(f => ({ ...f, phone_number: prefilledPhone }));
    }
  }, [prefilledPhone]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error on input change
  };

  const validateForm = () => {
    if (!termsAccepted) {
      return 'Please accept the User Agreement / Terms of Use and Privacy Policy to continue.';
    }

    if (!formData.email || !formData.password || !formData.phone_number) {
      return 'Please fill in all required fields';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return 'Please enter a valid email address';
    }

    const digits = formData.phone_number.replace(/\D/g, '');
    if (digits.length < 10) {
      return 'Please enter a valid mobile number (min 10 digits)';
    }

    if (formData.password.length < 6) {
      return 'Password must be at least 6 characters long';
    }

    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...signupData } = formData;
      const dataToSend = {
        ...signupData,
        username: formData.email,
        phone_number: formData.phone_number || undefined,
      };
      await signup(dataToSend);
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        first_name: '',
        last_name: '',
        phone_number: '',
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      first_name: '',
      last_name: '',
      phone_number: '',
    });
    setError('');
    setTermsAccepted(false);
    onClose();
  };

  React.useEffect(() => {
    if (window.AppleID) {
      window.AppleID.auth.init({
        clientId: process.env.REACT_APP_APPLE_SERVICE_ID || 'com.yovotrip.web',
        scope: 'name email',
        redirectURI: `${window.location.origin}/api/auth/apple`,
        usePopup: true,
      });
    }
  }, []);

  const handleAppleSignup = async () => {
    if (!termsAccepted) {
      setError('Please accept the User Agreement / Terms of Use and Privacy Policy first.');
      return;
    }
    try {
      setAppleLoading(true);
      setError('');
      const response = await window.AppleID.auth.signIn();
      const { id_token } = response.authorization;
      const fullName = response.user
        ? { firstName: response.user.name?.firstName, lastName: response.user.name?.lastName }
        : undefined;
      await appleLogin(id_token, fullName);
      handleClose();
    } catch (err) {
      if (err?.error === 'popup_closed_by_user') return;
      setError(err.message || 'Apple sign up failed. Please try again.');
    } finally {
      setAppleLoading(false);
    }
  };

  const triggerGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setError('');
      setGoogleLoading(true);
      try {
        await googleLogin(tokenResponse.access_token);
        handleClose();
      } catch (err) {
        setError(err.message || 'Google sign up failed. Please try again.');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => {
      setError('Google sign up was cancelled or failed. Please try again.');
    },
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        timeout: 0
      }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          width: { 
            xs: '90vw', 
            sm: '592px', 
            md: '592px',
            lg: '592px'
          },
          maxWidth: '592px',
          display: 'flex',
          flexDirection: 'column',
          transition: 'none !important',
          m: 2
        }
      }}
      slotProps={{
        backdrop: {
          sx: {
            transition: 'opacity 0ms !important'
          }
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pb: 1,
          pt: 2,
          px: 3,
          flexDirection: 'column',
          gap: 0.5,
          position: 'relative'
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            padding: '4px',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          <CloseIcon sx={{ fontSize: '20px', color: '#20202A' }} />
        </IconButton>
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '20px',
            fontWeight: 700,
            color: '#20202A',
            textAlign: 'center',
            width: '100%'
          }}
        >
          Sign Up
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            color: '#666',
            textAlign: 'center',
            width: '100%'
          }}
        >
          Create your Yovotrip account
        </Typography>
      </DialogTitle>

      <DialogContent 
        sx={{ 
          px: 3, 
          py: 1.5, 
          pt: 1.5,
          flex: 1,
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: '#20202A transparent',
          '&.MuiDialogContent-root': {
            overflowY: 'auto'
          },
          '&::-webkit-scrollbar': {
            width: '4px'
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#20202A',
            borderRadius: '2px'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#20202A'
          }
        }}
      >
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {error && (
            <Alert severity="error" sx={{ fontFamily: 'Host Grotesk', py: 0.5, px: 1.5 }}>
              {error}
            </Alert>
          )}

          <TextField
            name="email"
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            required
            disabled={loading}
            sx={{
              mt: '8px',
              '& .MuiOutlinedInput-root': {
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                backgroundColor: 'rgba(232, 233, 237, 1)',
                borderRadius: '8px',
                padding: '6px 10px',
                '& fieldset': {
                  borderColor: 'transparent'
                },
                '&:hover fieldset': {
                  borderColor: '#20202A'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#20202A',
                  borderWidth: '0.5px'
                },
                '& .MuiInputBase-input': {
                  padding: '10px 0',
                  lineHeight: '1.5',
                  fontSize: { xs: '16px', sm: '16px', md: '14px' },
                }
              },
              '& .MuiInputLabel-root': {
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                color: '#20202A',
                top: '-2px',
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                  transformOrigin: 'top left'
                }
              }
            }}
          />

          <TextField
            name="phone_number"
            label="Mobile Number"
            type="tel"
            value={formData.phone_number}
            onChange={(e) => {
              let val = e.target.value.replace(/[^\d+]/g, '');
              if (val.indexOf('+') > 0) val = val.replace(/\+/g, '');
              setFormData({ ...formData, phone_number: val });
              setError('');
            }}
            fullWidth
            required
            disabled={loading || !!prefilledPhone}
            placeholder="+91 9876543210"
            inputProps={{ maxLength: 15, inputMode: 'tel' }}
            sx={{
              mt: '8px',
              '& .MuiOutlinedInput-root': {
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                backgroundColor: 'rgba(232, 233, 237, 1)',
                borderRadius: '8px',
                padding: '6px 10px',
                '& fieldset': {
                  borderColor: 'transparent'
                },
                '&:hover fieldset': {
                  borderColor: '#20202A'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#20202A',
                  borderWidth: '0.5px'
                },
                '& .MuiInputBase-input': {
                  padding: '10px 0',
                  lineHeight: '1.5',
                  fontSize: { xs: '16px', sm: '16px', md: '14px' },
                }
              },
              '& .MuiInputLabel-root': {
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                color: '#20202A',
                top: '-2px',
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                  transformOrigin: 'top left'
                }
              }
            }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              name="first_name"
              label="First Name"
              type="text"
              value={formData.first_name}
              onChange={handleChange}
              fullWidth
              disabled={loading}
              sx={{
                mt: '8px',
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                  fontSize: '14px',
                  backgroundColor: 'rgba(232, 233, 237, 1)',
                  borderRadius: '8px',
                  padding: '4px 8px',
                  '& fieldset': {
                    borderColor: 'transparent'
                  },
                  '&:hover fieldset': {
                    borderColor: '#20202A'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#20202A',
                    borderWidth: '0.5px'
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 0',
                    lineHeight: '1.5',
                    fontSize: { xs: '16px', sm: '16px', md: '14px' },
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                  fontSize: '14px',
                  color: '#20202A',
                  top: '-2px',
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                    transformOrigin: 'top left'
                  }
                }
              }}
            />

            <TextField
              name="last_name"
              label="Last Name"
              type="text"
              value={formData.last_name}
              onChange={handleChange}
              fullWidth
              disabled={loading}
              sx={{
                mt: '8px',
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                  fontSize: '14px',
                  backgroundColor: 'rgba(232, 233, 237, 1)',
                  borderRadius: '8px',
                  padding: '4px 8px',
                  '& fieldset': {
                    borderColor: 'transparent'
                  },
                  '&:hover fieldset': {
                    borderColor: '#20202A'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#20202A',
                    borderWidth: '0.5px'
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 0',
                    lineHeight: '1.5',
                    fontSize: { xs: '16px', sm: '16px', md: '14px' },
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                  fontSize: '14px',
                  color: '#20202A',
                  top: '-2px',
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                    transformOrigin: 'top left'
                  }
                }
              }}
            />
          </Box>

          <TextField
            name="password"
            label="Password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            fullWidth
            required
            disabled={loading}
            helperText="Minimum 6 characters"
            sx={{
              mt: '8px',
              '& .MuiOutlinedInput-root': {
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                backgroundColor: 'rgba(232, 233, 237, 1)',
                borderRadius: '8px',
                padding: '6px 10px',
                '& fieldset': {
                  borderColor: 'transparent'
                },
                '&:hover fieldset': {
                  borderColor: '#20202A'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#20202A',
                  borderWidth: '0.5px'
                },
                '& .MuiInputBase-input': {
                  padding: '10px 0',
                  lineHeight: '1.5',
                  fontSize: { xs: '16px', sm: '16px', md: '14px' },
                }
              },
              '& .MuiInputLabel-root': {
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                color: '#20202A',
                top: '-2px',
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                  transformOrigin: 'top left'
                }
              },
              '& .MuiFormHelperText-root': {
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '12px'
              }
            }}
          />

          <TextField
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            fullWidth
            required
            disabled={loading}
            sx={{
              mt: '8px',
              '& .MuiOutlinedInput-root': {
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                backgroundColor: 'rgba(232, 233, 237, 1)',
                borderRadius: '8px',
                padding: '6px 10px',
                '& fieldset': {
                  borderColor: 'transparent'
                },
                '&:hover fieldset': {
                  borderColor: '#20202A'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#20202A',
                  borderWidth: '0.5px'
                },
                '& .MuiInputBase-input': {
                  padding: '10px 0',
                  lineHeight: '1.5',
                  fontSize: { xs: '16px', sm: '16px', md: '14px' },
                }
              },
              '& .MuiInputLabel-root': {
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                color: '#20202A',
                top: '-2px',
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                  transformOrigin: 'top left'
                }
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          pt: 1.5,
          flexDirection: 'column',
          gap: 1,
          borderTop: 'none'
        }}
      >
        {/* Terms & Privacy Consent Checkbox */}
        <Box sx={{ width: '100%', mb: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={loading}
                size="small"
                sx={{
                  color: '#20202A',
                  '&.Mui-checked': { color: '#20202A' },
                  p: 0,
                  mt: '1px',
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '12px', color: '#666', lineHeight: 1.4, m: 0 }}>
                I agree to YovoTrip's{' '}
                <RouterLink 
                  to="/user-agreement" 
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: '#1565C0', textDecoration: 'underline' }}
                >
                  User Agreement / Terms of Use
                </RouterLink>{' '}
                and acknowledge the{' '}
                <RouterLink 
                  to="/privacy-policy" 
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: '#1565C0', textDecoration: 'underline' }}
                >
                  Privacy Policy
                </RouterLink>
                .
              </Typography>
            }
            sx={{
              alignItems: 'flex-start',
              m: 0,
              gap: '8px',
              width: '100%',
              '& .MuiFormControlLabel-label': {
                m: 0,
                pt: 0,
              },
            }}
          />
          <Typography sx={{ fontSize: '11px', color: '#999', ml: '26px', mt: 0.25 }}>
            Required to create your account.
          </Typography>
        </Box>

        <Button
          onClick={handleSubmit}
          variant="contained"
          fullWidth
          disabled={loading || !termsAccepted}
          sx={{
            backgroundColor: '#20202A',
            color: '#FBFBFB',
            borderRadius: '18px',
            padding: '10px 24px',
            fontSize: '16px',
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: 'Host Grotesk',
            minHeight: '44px',
            '&:hover': {
              backgroundColor: '#333'
            },
            '&:disabled': {
              backgroundColor: '#ccc'
            }
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Sign Up'}
        </Button>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 0.5, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
            <Typography
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                color: 'rgba(126, 128, 143, 1)',
                fontWeight: 400,
                whiteSpace: 'nowrap'
              }}
            >
              Or sign up with
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, width: '100%', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => {
                if (!termsAccepted) {
                  setError('Please accept the User Agreement / Terms of Use and Privacy Policy first.');
                  return;
                }
                triggerGoogleLogin();
              }}
              disabled={loading || googleLoading || appleLoading}
              sx={{
                flex: 1,
                maxWidth: '120px',
                height: '44px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                color: '#20202A',
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                textTransform: 'none',
                backgroundColor: 'rgba(232, 233, 237, 1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                '&:hover': {
                  borderColor: '#20202A',
                  backgroundColor: 'rgba(232, 233, 237, 0.8)'
                },
                '&:disabled': {
                  borderColor: '#e0e0e0',
                  backgroundColor: 'rgba(232, 233, 237, 0.5)'
                }
              }}
            >
              {googleLoading ? (
                <CircularProgress size={20} sx={{ color: '#20202A' }} />
              ) : (
                <img src={googleIcon} alt="Google" style={{ width: '24px', height: '24px' }} />
              )}
            </Button>
            <Button
              variant="outlined"
              onClick={handleAppleSignup}
              disabled={loading || googleLoading || appleLoading}
              sx={{
                flex: 1,
                maxWidth: '120px',
                height: '44px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                color: '#20202A',
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                textTransform: 'none',
                backgroundColor: 'rgba(232, 233, 237, 1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                '&:hover': {
                  borderColor: '#20202A',
                  backgroundColor: 'rgba(232, 233, 237, 0.8)'
                },
                '&:disabled': {
                  borderColor: '#e0e0e0',
                  backgroundColor: 'rgba(232, 233, 237, 0.5)'
                }
              }}
            >
              {appleLoading ? (
                <CircularProgress size={20} sx={{ color: '#20202A' }} />
              ) : (
                <img src={appleIcon} alt="Apple" style={{ width: '24px', height: '24px' }} />
              )}
            </Button>
          </Box>
        </Box>
        <Button
          onClick={() => {
            handleClose();
            if (onSwitchToLogin) onSwitchToLogin();
          }}
          variant="text"
          fullWidth
          disabled={loading}
          sx={{
            color: '#20202A',
            borderRadius: '8px',
            padding: '10px 24px',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          Already have an account? Login
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignupModal;

